import Service from '@ember/service';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import EmberEvented from '@ember/object/evented';
import { TrackedArray } from 'tracked-built-ins';
import nowPlayingMetadata from '../utils/now-playing-metadata';
import buildFeedFunc from '../utils/build-feed';
import { next } from '@ember/runloop';
import { action } from '@ember/object';
import { registerDestructor } from '@ember/destroyable';

export default class FeedsService extends Service.extend(EmberEvented) {
  @service stereo;
  @service store;
  @service clock;
  @service router;
  // @service settings;

  @tracked feeds = new TrackedArray([]);
  @tracked currentBroadcast;
  @tracked currentPlayback;
  @tracked current;

  constructor() {
    super(...arguments);
    registerDestructor(this, () => {
      this.stereo.off('current-sound-changed', this.onCurrentSoundChange);
    });

    this.stereo.on('current-sound-changed', this.onCurrentSoundChange);
  }

  buildFeeds(feedRecords, options = {}) {
    this.records = feedRecords;
    return feedRecords.map(record => this.buildFeed(record, options));
  }

  findFeed(params = {}) {
    return this.feeds.find(feed => feed.matches(params));
  }

  buildFeed(feedRecord, options = {}) {
    let existingFeed = this.feeds.find(feed => feed.matches({feedRecord}));
    if (existingFeed) {
      return existingFeed
    } else {
      let feed = buildFeedFunc(feedRecord, {...options, owner: this })
      next(() => {
        this.feeds.push(feed);
      });
      return feed;
    }
  }

  playFeed(feedRecord, startTime) {
    this.playFeedTask.perform(feedRecord, startTime);
  }

  playFeedTask = task({restartable: true}, async (feedRecord, startTime) => {
    let feed = this.buildFeed(feedRecord);
    await feed.playTask.perform(startTime)
  });

  @action
  onCurrentSoundChange({sound, previousSound}) {
    let currentFeed = this.findFeed({url: sound.identifier});
    if (currentFeed) {
      // This allows us to toggle between playback and live feeds easily
      this.selectFeed(currentFeed);
    }
  }

  selectFeed(feed) {
    if (feed) {
      this.current = feed;
      if (feed.kind == 'broadcast') {
        this.currentBroadcast = feed;
      } else if (feed.kind == 'playback') {
        this.currentPlayback = feed;
      }
    }
  }

  updateAudioMetadata = task(async (url, recordMap) => {
    let currentMetadata = this.stereo.metadataCache.find(url);
    let {
      nowPlaying,
      airing,
      item,
      show,
      track,
      note,
      traffic,
      station,
      broadcast,
    } = recordMap;

    item = [item, track, note, traffic]
      .filter((i) => !!i)
      .sort((a, b) => {
        return a.playedAt < b.playedAt ? 1 : -1;
      })[0];

    // select most recent item

    let objects = {
      airing,
      show: show || airing?.show,
      station: station || airing?.station,
      broadcast: broadcast || airing?.broadcast,
      item: item,
      note: undefined,
      track: undefined,
      traffic: undefined,
      itemType: item?.type,
    };

    if (item) {
      if (item.type) {
        objects[item.type] = item;
      } else {
      }
    }

    let metadata = {
      ...currentMetadata,
      ...objects,
    };

    metadata = { ...metadata, ...nowPlayingMetadata(metadata) };
    metadata.isLive =
      metadata?.airing?.startsAt < this.clock.date &&
      metadata?.airing?.endsAt > this.clock.date;

    this.stereo.metadataCache.store(url, metadata);
    await this.trigger('change', { url, metadata });

    return { url, metadata };
  });
}
